import {
  AlignItems,
  AppStoreIcon,
  Body1,
  Box,
  Breakpoint,
  Color,
  Display,
  FlexDirection,
  Heading2,
  Icon,
  IconVariant,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { IconNames } from '@gousto-internal/citrus-react'
import type { DashboardExperienceDownloadAppResponse } from '@library/api-dashboard-experience'
import React from 'react'

import { useDashboardActionTracking } from '../hooks/useDashboardActionTracking'

type DownloadAppActionProps = {
  downloadApp: DashboardExperienceDownloadAppResponse
}

const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=uk.co.gousto.gousto&utm_source=global_co&utm_medium=prtnr&utm_content=Mar2515&utm_campaign=PartBadge&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1'
const APP_STORE_LINK =
  'https://geo.itunes.apple.com/gb/app/gousto-uks-no1-recipe-kit/id992607465?mt=8'
const STAR_COUNT = 5

const DownloadAppContainer = withCitrusTheme('article', (theme) => ({
  backgroundColor: theme.color(Color.White),
  border: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  borderRadius: '6px',
  display: 'block',
  overflow: 'hidden',
  padding: `${theme.spacing(4)}px`,
  width: '100%',
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    display: 'none',
  },
}))

const StoreBadgeLink = withCitrusTheme('a', (theme) => ({
  display: 'block',
  lineHeight: 0,
  maxWidth: '151px',
  width: `calc((100% - ${theme.spacing(3)}px) / 2)`,
  '&:focus': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

const StarsContainer = withCitrusTheme('div', (theme) => ({
  color: theme.color(Color.Transparent),
  display: 'inline-flex',
  position: 'relative',
}))

const StarsOverlay = withCitrusTheme('div', (theme) => ({
  color: theme.color(Color.ColdGrey_800),
  left: 0,
  overflow: 'hidden',
  position: 'absolute',
  top: 0,
}))

const StarsLayer = withCitrusTheme('div', () => ({
  display: 'flex',
  flexShrink: 0,
  width: 'max-content',
}))

const getRatingPercentage = (rating: number) =>
  `${(Math.max(0, Math.min(rating, STAR_COUNT)) / STAR_COUNT) * 100}%`

function RatingStars({ rating }: { rating: number }) {
  const ratingPercentage = getRatingPercentage(rating)
  const stars = Array.from({ length: STAR_COUNT }, (_, index) => (
    <Icon key={index} name="rate_full" size={8} variant={IconVariant.Inherit} />
  ))

  return (
    <StarsContainer aria-label={`${rating} out of ${STAR_COUNT} stars`} role="img">
      <StarsLayer aria-hidden="true">{stars}</StarsLayer>
      <StarsOverlay
        aria-hidden="true"
        data-testid="download-app-stars-filled"
        style={{ width: ratingPercentage }}
      >
        <StarsLayer>{stars}</StarsLayer>
      </StarsOverlay>
    </StarsContainer>
  )
}

export function DownloadAppAction({ downloadApp }: DownloadAppActionProps) {
  const { trackAppDownloadButtonClick } = useDashboardActionTracking()

  return (
    <DownloadAppContainer data-testid="download-app-action">
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={4} width="100%">
        <Heading2 color={Color.ColdGrey_800} size={[3, 5]}>
          {downloadApp.title}
        </Heading2>

        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          {downloadApp.bullets.map((bullet) => (
            <Box
              alignItems={AlignItems.Center}
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              gap={3}
              key={`${bullet.icon}-${bullet.title}`}
            >
              <Box color={Color.ColdGrey_800} display={Display.Flex} style={{ flexShrink: 0 }}>
                <Icon name={bullet.icon as IconNames} size={8} variant={IconVariant.Inherit} />
              </Box>
              <Body1 color={Color.ColdGrey_800}>{bullet.title}</Body1>
            </Box>
          ))}
        </Box>

        <Box
          alignItems={AlignItems.Center}
          display={Display.Flex}
          gap={3}
          justifyContent={JustifyContent.Center}
        >
          <RatingStars rating={downloadApp.stars.rating} />
          <Body1 color={Color.ColdGrey_800}>{downloadApp.stars.value}</Body1>
        </Box>

        <Box
          alignItems={AlignItems.Center}
          display={Display.Flex}
          gap={3}
          justifyContent={JustifyContent.Center}
          width="100%"
        >
          <StoreBadgeLink
            aria-label="Download the app on Google Play"
            href={GOOGLE_PLAY_LINK}
            onClick={trackAppDownloadButtonClick}
          >
            <AppStoreIcon name="google" width="100%" />
          </StoreBadgeLink>
          <StoreBadgeLink
            aria-label="Download the app on the App Store"
            href={APP_STORE_LINK}
            onClick={trackAppDownloadButtonClick}
          >
            <AppStoreIcon name="apple" width="100%" />
          </StoreBadgeLink>
        </Box>
      </Box>
    </DownloadAppContainer>
  )
}
